import * as colors from 'scoreboard-colors'
import { path } from 'd3-path'
import pointer from './helpers/pointer.js'
import {
  calculateSubcurve,
  isPointOnCurve,
  mirrorCurve,
  solveBezierForX,
  solveBezierForY
} from 'silkpath'
import Symbol2d from '../2d.js'

const segments = 4
const theta = (4 / 3) * Math.tan(Math.PI / (2 * segments))

const right = (_path, intersection, curves, pointerConfig) => {
  const _pointer = pointer(_path, pointerConfig.dimensions, pointerConfig.arrowHandle, {
    height: pointerConfig.pointerHeight,
    width: pointerConfig.arrowWidth,
  })

  // Draw the right outer curve
  _path.lineTo(...curves.right.outer[0])

  const pointerTop = pointerConfig.pointerBounds.height - _pointer.height

  // We need to calculate `x` and `t` for the right outer curve where the pointer starts
  const intersectionRight = solveBezierForX(curves.right.outer, pointerTop)
  const controls0 = calculateSubcurve(1 - intersectionRight.t, ...curves.right.outer.slice().reverse()).controls
  _path.bezierCurveTo(...controls0[1], ...controls0[0], ...intersectionRight.point)

  // Draw the pointer
  _pointer(intersectionRight.point, 'south', pointerConfig.pointerBounds, pointerConfig.pointerTip)

  const intersectionInnerBottom = solveBezierForX(curves.right.inner, pointerTop)
  const controls1 = calculateSubcurve(1 - intersectionInnerBottom.t, ...curves.right.inner.slice().reverse())

  let points = [controls1.start, controls1.controls[0], controls1.controls[1], curves.right.inner[0]]
  const intersectionInnerTop = solveBezierForX(points.reverse(), intersection.point[1])
  const innerSubCurve = calculateSubcurve(intersectionInnerTop.t, ...points.slice())
  _path.bezierCurveTo(...innerSubCurve.controls[1], ...innerSubCurve.controls[0], ...innerSubCurve.start)

  return _path
}

const left = (_path, intersection, curves, pointerConfig) => {
  // Draw the left inner curve, starting at the intersection
  const controls = calculateSubcurve(intersection.t, ...curves.left.inner).controls // These are going from the top to the bottom
  const inner = [intersection.point, controls[0], controls[1], curves.left.inner.at(-1)] // This is starting at the intersection and going to the bottom

  // Calculate `x` where the curve intersects the bottom of the base
  const intersectionBaseRight = solveBezierForX(inner, pointerConfig.baseBottom)
  // Let's reverse the right curve and calculate new points assuming it starts at the intersectionBaseRight
  const innerSubCurve = calculateSubcurve(1 - intersectionBaseRight.t, ...inner.slice().reverse())
  _path.bezierCurveTo(...innerSubCurve.controls[1], ...innerSubCurve.controls[0], ...innerSubCurve.start)

  // Calculate `x` where the curve intersects the bottom of the base on the left side
  const intersectionBaseLeft = solveBezierForX(curves.left.outer, pointerConfig.baseBottom)
  _path.lineTo(...intersectionBaseLeft.point)

  const outerSubCurve = calculateSubcurve(1 - intersectionBaseLeft.t, ...curves.left.outer.slice().reverse())
  _path.bezierCurveTo(...outerSubCurve.controls[0], ...outerSubCurve.controls[1], ...curves.left.outer[0])

  return _path
}

// The main curve is the 2nd half of the parabola, starting at the top and ending at the bottom. It's the inner of the right side
const getMainCurve = (dimensions, intersection0, intersection1, threshold = 1) => {
  let x = intersection1[0] // We know it's at least this far
  let arcWidth = x / 2
  let curve = null

  // Build a best guess of the curve before we start testing it with the intersection points
  let test = [
    [arcWidth, 0], // start
    [arcWidth + theta * arcWidth, 0], // first control point
    [x, dimensions.height - theta * dimensions.height], // second control point
    [x, dimensions.height], // end
  ]

  // Determine where to start the offset
  let offset = Math.abs(solveBezierForX(test, intersection0[1]).point[0] - arcWidth * 2) / 2

  while (!curve && x < dimensions.width * 2) { // Need to increase the width before we add constraint to make sure this curve doesn't end up outside the right bounds
    let retest = false

    arcWidth = x / 2

    test = [
      [arcWidth - offset, 0], // start
      [arcWidth - offset + theta * arcWidth, 0], // first control point
      [x, dimensions.height - theta * dimensions.height], // second control point
      [x, dimensions.height], // end
    ]

    // Check if the intersection0 point is on the mirror of the curve
    if (!isPointOnCurve(mirrorCurve(test, test[0][0]), intersection0, threshold)) {
      // Adjusting the left side offset
      offset += 1
      retest = true
    }

    if (!isPointOnCurve(test, intersection1, threshold)) {
      // Adjusting the right side, so increase x
      x += 1
      retest = true
    }

    if (!retest) {
      curve = test
    }
  }

  return curve
}

export default class FoldedArrow extends Symbol2d {

  onDimensionsChange (dimensions, handles) {
    if (dimensions.diff.width) {
      // Width is changing, adjust the tip height
      this.properties.arrowHeight += dimensions.diff.height * this.properties.arrowHeight
      this.properties.arrowWidth += dimensions.diff.width * this.properties.arrowWidth

      if (dimensions.height > dimensions.width) {
        // When the height is greater than the width, we want to scale the height
        this.properties.arrowHeight += dimensions.diff.width * this.properties.arrowHeight
      }
    }

    if (dimensions.diff.height && dimensions.height < dimensions.width) {
      // Height is changing and the width is greater than the height
      this.properties.arrowHeight += dimensions.diff.height * this.properties.arrowHeight
    }

    this.updateHandles(handles, dimensions)
  }

  getPointerTip = (curves, pointerY) => {
    const arrowWidth = curves.right.outer.at(-1)[0] - curves.right.inner.at(-1)[0]
    const pointerBottomIntersect = solveBezierForX(curves.right.outer, pointerY).point

    return [pointerBottomIntersect[0] - arrowWidth / 2, pointerBottomIntersect[1]]
  }

  getCurvePaths (dimensions, curves, baseBottom = dimensions.height, pointerHeight, pointerBounds) {
    const arrowWidth = curves.right.outer.at(-1)[0] - curves.right.inner.at(-1)[0]

    if (!pointerBounds) {
      pointerBounds = {
        height: dimensions.height,
      }
    }

    const pointerBottomIntersect = solveBezierForX(curves.right.outer, pointerBounds.height).point

    const pointerConfig = {
      baseBottom,
      dimensions,
      pointerHeight,
      pointerBounds,
      arrowWidth,
      pointerTip: [pointerBottomIntersect[0] - arrowWidth / 2, pointerBottomIntersect[1]], // coordinates of the tip of the pointer
    }

    // Get the intersection of the the top of the two curves
    const intersection = solveBezierForY(curves.right.inner, curves.right.outer[0][0] - arrowWidth / 2)

    return {
      outline: this.getOutlinePath(curves, pointerConfig, intersection),
      foreground: this.getForegroundPath(curves, pointerConfig, intersection),
      background: this.getBackgroundPath(curves, pointerConfig, intersection),
    }
  }

  getOutlinePath (curves, pointerConfig, intersection) {
    const _path = path()

    // Start at the top center of the symbol
    _path.moveTo(...curves.left.outer[0])

    // Move to where the right outer curve starts
    _path.lineTo(intersection.point[0], 0)

    // Draw each side
    right(_path, intersection, curves, pointerConfig)
    left(_path, intersection, curves, pointerConfig)

    _path.closePath()
    return _path.toString()
  }

  getForegroundPath (curves, pointerConfig, intersection) {
    const _path = path()

    // Start at the top center of the symbol
    _path.moveTo(...curves.left.outer[0])
    _path.lineTo(intersection.point[0], 0)

    right(_path, {point: [null, 0]}, curves, pointerConfig)

    _path.closePath()
    return _path.toString()
  }

  getBackgroundPath (curves, pointerConfig, intersection) {
    const _path = path()

    _path.moveTo(...curves.left.outer[0])

    // Curve down to where we intersect the right inner curve
    const controls = calculateSubcurve(1 - intersection.t, ...curves.right.inner.slice().reverse()).controls
    _path.bezierCurveTo(...controls[1], ...controls[0], ...intersection.point)

    // Draw the remaining left path clockwise
    left(_path, intersection, curves, pointerConfig)
    _path.closePath()
    return _path.toString()
  }

  /*
   * Flips the location of the pointer arpit handle x location.
   * This is used by left facing arrows to invert the handle locations before
   * determining constraints.
   */
  _invertHandles (handles) {
    const _handles = structuredClone(handles)
    _handles[0][0] = 1 - _handles[0][0]
    return _handles
  }

  getCurveData (dimensions, arrowWidth, intersection0, intersection1, pointerY) {
    const maxThreshold = 10 // The maximum threshold to use when finding the curve when determining point + curve intersections
    let curve = null
    let threshold = 1
    while (!curve && threshold < maxThreshold) {
      // Find the curve that intersects the top and bottom of the arrow
      // We increase the point + curve intersection threshold until we find a curve that intersects the top and bottom of the arrow
      curve = getMainCurve(dimensions, intersection0, intersection1, threshold++)
    }

    const mirror = mirrorCurve(curve, curve[0][0])
    const offsetArrowWidth = point => [point[0] + arrowWidth, point[1]]

    const data = {
      right: {
        outer: curve.map(offsetArrowWidth),
        inner: curve
      },
      left: {
        inner: mirror.map(offsetArrowWidth),
        outer: mirror
      }
    }

    data.tipPoint = this.getPointerTip(data, pointerY)
    return data
  }

  getComponents (data, dimensions) {
    if (!this._constraints) {
      const pointerHeight = dimensions.height * this.handles[1].defaults[1] - dimensions.height * this.handles[0].defaults[1]
      this._constraints = {
        x: this._setXConstraints(dimensions, pointerHeight, this.handles.map(h => h.defaults)),
        y: this._setYConstraints(dimensions, pointerHeight, this.handles.map(h => h.defaults))
      }
    }

    const handles = this.getHandles(dimensions)
    const _handles = handles.map(handle => handle.coords)
    if (!this.properties) {
      // Initial render
      this.properties = this.getProperties(dimensions, _handles)
    }

    // We're building a parabola going from the bottom left corner and rounding down to the intersection1
    // bottom left corner
    const intersection0 = [0, dimensions.height]
    // intersection1 is the point where the curve intersects the left side inner arm pit (adjacent the base of the arrow)
    const intersection1 = [dimensions.width - (this.properties.arrowWidth + this.properties.arrowWidth / 2), dimensions.height * handles[0].coords[1]]

    this.curveData = this.getComponentCurveData(dimensions, this.properties.arrowWidth, intersection0, intersection1, dimensions.height * handles[1].coords[1])

    this.updateHandles(_handles, dimensions)

    const paths = this.getPaths(data, dimensions, _handles)

    return {
      handles,
      flattenedPaths: paths.length > 1 ? this.getFlattenedPaths(data, dimensions) : paths,
      paths
    }
  }

  getComponentCurveData (dimensions, arrowWidth, intersection0, intersection1, pointerY) {
    return this.getCurveData(dimensions, arrowWidth, intersection0, intersection1, pointerY)
  }

  getPaths (data, dimensions, handles) {
    const color = data.color // the back half is darker
    const backColor = colors.adjustLightness(color, -4) // the back half is darker
    const lidColor = colors.adjustLightness(color, 20) // the front half is lighter

    const id0 = `${this.id}-0-component`
    const id1 = `${this.id}-1-component`

    const { background, foreground, outline }= this.generateCurvePaths(data, dimensions, handles)

    // The first component is the back arrow
    return [{
      path: background,
      id: id0,
      fill: this.getFillValue(id0, data.fill, backColor),
      gradient: {
        type: 'linearGradient',
        id: `shape-gradient-0-${this.id}`,
        x1: this.reverseGradient ? '100%' : '0%', // The back arrow gradient is reversed for left facing
        y1: '100%',
        x2: '50%',
        y2: '0%',
        stops: [
          { offset: '0%', color: color },
          { offset: '60%', color: colors.adjustLightness(color, -5) },
          { offset: '80%', color: colors.adjustLightness(color, -10) },
          { offset: '100%', color: colors.adjustLightness(color, -20) },
        ]
      }
    }, {
      // The second component is the front arrow, so it's on top
      path: foreground,
      id: id1,
      fill: this.getFillValue(id1, data.fill, lidColor),
      gradient: {
        type: 'linearGradient',
        id: `shape-gradient-1-${this.id}`,
        x1: '0%',
        y1: '0%',
        x2: '0%',
        y2: '100%',
        stops: [
          { offset: '0%', color: colors.adjustLightness(color, 50) },
          { offset: '50%', color: colors.adjustLightness(color, 20) },
          { offset: '100%', color: color },
        ]
      }
    }, {
      path: outline,
      fill: 'none',
      styles: {
        'stroke': 'none',
      }
    }]
  }

  /**
   * Finds the constraint value (x or y) for positioning the arrow handle based on the given dimensions, pointer height, and current handle positions.
   *
   * @param {Object} dimensions - The dimensions of the arrow.
   * @param {number} dimensions.width - The width of the arrow.
   * @param {number} dimensions.height - The height of the arrow.
   * @param {number} pointerHeight - The height of the arrow pointer.
   * @param {Array} handles - An array containing the current positions of the arrow handles.
   * @param {Array} handles[0] - The position of the first handle (arm of the arrow).
   * @param {number} handles[0][0] - The x-coordinate of the first handle.
   * @param {number} handles[0][1] - The y-coordinate of the first handle.
   * @param {string} type - The type of constraint to find ('x' or 'y').
   * @param {number} increment - The step size for adjusting the constraint value.
   *
   * @returns {number} The constraint value that produces a valid arrow curve.
   *
   * @example
   * const dimensions = { width: 200, height: 150 }
   * const pointerHeight = 20
   * const handles = [[0.8, 0.5]]
   * const xConstraint = findConstraint(dimensions, pointerHeight, handles, 'x', 0.01)
   * const yConstraint = findConstraint(dimensions, pointerHeight, handles, 'y', 0.01)
   */
  findConstraint (dimensions, pointerHeight, handles, type, increment = 0.01) {
    let result = type === 'x' ? 0.99 : 0.01 // Start value depends on the type
    let curveData = null
    let arrowWidth
    let intersection1

    while (!curveData) {
      if (type === 'x') {
        arrowWidth = (dimensions.width - dimensions.width * result) * 2
        intersection1 = [dimensions.width - (arrowWidth + arrowWidth / 2), dimensions.height * handles[0][1]]
      } else { // 'y'
        arrowWidth = (dimensions.width - dimensions.width * handles[0][0]) * 2
        intersection1 = [dimensions.width - (arrowWidth + arrowWidth / 2), dimensions.height * result]
      }

      curveData = this.getCurveData(dimensions, arrowWidth, [0, dimensions.height], intersection1, dimensions.height * handles[0][1] + pointerHeight)

      if (curveData?.tipPoint[0] > dimensions.width) {
        curveData = null // Invalid curve, reset and continue
      }

      if (!curveData) {
        if (type === 'x') {
          result -= increment // Decrement for x
        } else {
          result += increment // Increment for y
        }
      }
    }

    return result
  }

}
